import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Search, 
  Users, 
  X, 
  Plus,
  Calendar,
  FileText,
  Trash2,
  Check,
  Grid3X3,
  List
} from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { toast } from '@/hooks/use-toast';

interface CandidateListsModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidateId: string;
  candidateName: string;
}

// Helper function to get current user's organization
const getCurrentOrganization = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data: userOrgs } = await supabase
    .from('user_organization')
    .select('organization_uuid')
    .eq('user_uuid', user.id)
    .single();

  if (!userOrgs) throw new Error('User not associated with any organization');
  return userOrgs.organization_uuid;
};

// Hook to get lists for candidate
const useCandidateLists = (candidateId: string) => {
  return useQuery({
    queryKey: ['candidate-lists', candidateId],
    queryFn: async () => {
      const organization = await getCurrentOrganization();
      if (!organization) {
        throw new Error('No organization found');
      }

      // Get all lists in the organization
      const { data: allLists, error: listsError } = await supabase
        .from('lists_with_candidate_count')
        .select('*')
        .eq('organization_uuid', organization)
        .order('created_at', { ascending: false });

      if (listsError) {
        throw listsError;
      }

      // Get lists that contain this candidate
      const { data: candidateLists, error: candidateListsError } = await supabase
        .from('list_members')
        .select('list_id')
        .eq('candidate_id', candidateId);

      if (candidateListsError) {
        throw candidateListsError;
      }

      const candidateListIds = new Set(candidateLists?.map(cl => cl.list_id) || []);

      // Separate lists into "in" and "not in"
      const listsIn = allLists?.filter(list => candidateListIds.has(list.id)) || [];
      const listsNotIn = allLists?.filter(list => !candidateListIds.has(list.id)) || [];

      return {
        listsIn,
        listsNotIn,
        allLists: allLists || []
      };
    },
    enabled: !!candidateId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const CandidateListsModal = ({
  isOpen,
  onClose,
  candidateId,
  candidateName
}: CandidateListsModalProps) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);

  const { data: listsData, isLoading, error, refetch } = useCandidateLists(candidateId);

  // Filter lists based on search term
  const filteredListsNotIn = useMemo(() => {
    if (!listsData?.listsNotIn) return [];
    const lowerSearchTerm = searchTerm.toLowerCase();
    return listsData.listsNotIn.filter(list => 
      list.name.toLowerCase().includes(lowerSearchTerm) ||
      (list.description && list.description.toLowerCase().includes(lowerSearchTerm))
    );
  }, [listsData?.listsNotIn, searchTerm]);

  const handleListToggle = (listId: string) => {
    setSelectedLists(prev =>
      prev.includes(listId)
        ? prev.filter(id => id !== listId)
        : [...prev, listId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLists.length === filteredListsNotIn.length) {
      setSelectedLists([]);
    } else {
      setSelectedLists(filteredListsNotIn.map(l => l.id));
    }
  };

  const handleAddToLists = async () => {
    if (selectedLists.length === 0) return;

    setIsAdding(true);
    try {
      // Get current user for added_by field
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Add candidate to all selected lists
      for (const listId of selectedLists) {
        const { error } = await supabase
          .from('list_members')
          .insert({
            list_id: listId,
            candidate_id: candidateId,
            added_by: user.id,
            added_at: new Date().toISOString()
          });

        if (error) {
          // Check if it's a duplicate entry error
          if (error.code === '23505') {
            console.log(`Candidate already in list ${listId}`);
            continue; // Skip this list and continue with others
          }
          throw error;
        }
      }

      toast({
        title: "Success",
        description: `Added ${candidateName} to ${selectedLists.length} list${selectedLists.length !== 1 ? 's' : ''}`,
      });
      
      setSelectedLists([]);
      refetch(); // Refresh the lists data
    } catch (error) {
      console.error('Error adding candidate to lists:', error);
      toast({
        title: "Error",
        description: "Failed to add candidate to lists",
        variant: "destructive",
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveFromList = async (listId: string) => {
    setIsRemoving(listId);
    try {
      await supabase
        .from('list_members')
        .delete()
        .eq('list_id', listId)
        .eq('candidate_id', candidateId);

      toast({
        title: "Success",
        description: `Removed ${candidateName} from list`,
      });
      
      refetch(); // Refresh the lists data
    } catch (error) {
      console.error('Error removing candidate from list:', error);
      toast({
        title: "Error",
        description: "Failed to remove candidate from list",
        variant: "destructive",
      });
    } finally {
      setIsRemoving(null);
    }
  };

  const handleClose = () => {
    setSelectedLists([]);
    setSearchTerm("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[95vh] flex flex-col [&>button]:hidden">
        <DialogHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                {t("candidates.lists.title", "Manage Lists for {{name}}", { name: candidateName })}
              </DialogTitle>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t("candidates.lists.description", "View lists this candidate is in and add them to more lists")}
              </p>
            </div>
            <DialogClose asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <X className="h-6 w-6" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="flex-1 flex flex-col space-y-6 min-h-0">
          {/* Lists the candidate is already in */}
          {listsData?.listsIn && listsData.listsIn.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                {t("candidates.lists.currentLists", "Currently in {{count}} lists", { count: listsData.listsIn.length })}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {listsData.listsIn.map((list) => (
                  <div
                    key={list.id}
                    className="relative bg-white dark:bg-gray-800 border border-green-200 rounded-sm p-4 hover:shadow-md transition-all duration-200"
                  >
                    {/* Remove Button */}
                    <div className="absolute top-3 right-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFromList(list.id)}
                        disabled={isRemoving === list.id}
                        className="text-red-600/70 hover:text-red-800 hover:bg-red-50 h-6 w-6 p-0"
                      >
                        {isRemoving === list.id ? (
                          <div className="animate-spin h-3 w-3 border-2 border-red-600 border-t-transparent rounded-full"></div>
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                      </Button>
                    </div>

                    {/* List Info */}
                    <div className="flex items-start space-x-3 mb-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-green-100 text-green-600">
                          {list.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{list.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Users className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                          <span className="text-sm text-gray-600 dark:text-gray-300">{list.candidate_count} candidates</span>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    {list.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">{list.description}</p>
                    )}

                    {/* Created Date */}
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Created {format(new Date(list.created_at), 'MMM d, yyyy')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add to more lists section */}
          <div className="flex-1 flex flex-col space-y-4 min-h-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-500" />
              {t("candidates.lists.addToMore", "Add to More Lists")}
            </h3>

            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder={t("candidates.lists.searchPlaceholder", "Search lists by name or description...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  onClick={() => setSearchTerm('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* View Toggle */}
            <div className="flex items-center justify-end gap-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">{t("common.view", "View")}:</span>
              <ToggleGroup
                type="single"
                value={viewMode}
                onValueChange={(value) => value && setViewMode(value as 'grid' | 'list')}
                className="h-8 bg-gray-50 dark:bg-gray-800/50 rounded-sm p-1"
              >
                <ToggleGroupItem 
                  value="grid" 
                  className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-700"
                >
                  <Grid3X3 className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem 
                  value="list" 
                  className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-700"
                >
                  <List className="h-4 w-4" />
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            {/* Results Count */}
            {searchTerm && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {t("candidates.lists.listsFound", "{{count}} lists found", { count: filteredListsNotIn.length })}
                </p>
              </div>
            )}

            {/* Lists to add to */}
            <div className="flex-1 min-h-0 overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-32">
                  <p className="text-red-600">{t("common.error", "Error loading lists")}</p>
                </div>
              ) : filteredListsNotIn.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {searchTerm
                        ? t("candidates.lists.noListsFound", "No lists found")
                        : t("candidates.lists.noMoreLists", "No more lists available")
                      }
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      {searchTerm 
                        ? t("candidates.lists.tryAdjustingSearch", "Try adjusting your search terms") 
                        : t("candidates.lists.candidateInAllLists", "This candidate is already in all available lists")
                      }
                    </p>
                  </div>
                </div>
              ) : (
                <div className="h-full overflow-y-auto pr-2">
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {filteredListsNotIn.map((list) => (
                                                 <div
                           key={list.id}
                           className={`relative bg-white dark:bg-gray-800 border rounded-sm cursor-pointer transition-all duration-200 hover:shadow-md ${
                             selectedLists.includes(list.id)
                               ? 'ring-2 ring-inset ring-blue-500 border-blue-500 p-4'
                               : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 p-4'
                           }`}
                           onClick={() => handleListToggle(list.id)}
                         >
                                                     {/* Selection Checkbox */}
                           <div className="absolute top-3 right-3">
                             <Checkbox
                               checked={selectedLists.includes(list.id)}
                               onChange={() => handleListToggle(list.id)}
                               className="h-4 w-4"
                             />
                           </div>

                          {/* List Info */}
                          <div className="flex items-start space-x-3 mb-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-blue-100 text-blue-500">
                                {list.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{list.name}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <Users className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                                <span className="text-sm text-gray-600 dark:text-gray-300">{list.candidate_count} candidates</span>
                              </div>
                            </div>
                          </div>

                          {/* Description */}
                          {list.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">{list.description}</p>
                          )}

                          {/* Created Date */}
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Created {format(new Date(list.created_at), 'MMM d, yyyy')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredListsNotIn.map((list) => (
                                               <div
                         key={list.id}
                         className={`flex items-center space-x-4 bg-white dark:bg-gray-800 border rounded-sm cursor-pointer transition-all duration-200 hover:shadow-sm ${
                           selectedLists.includes(list.id)
                             ? 'ring-2 ring-inset ring-blue-500 border-blue-500 p-4'
                             : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 p-4'
                         }`}
                         onClick={() => handleListToggle(list.id)}
                       >
                          <Checkbox
                            checked={selectedLists.includes(list.id)}
                            onChange={() => handleListToggle(list.id)}
                            className="h-4 w-4"
                          />
                          
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-blue-100 text-blue-500 text-xs">
                              {list.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100">{list.name}</h4>
                            {list.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{list.description}</p>
                            )}
                          </div>

                          <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-300">
                            <div className="flex items-center">
                              <Users className="h-3 w-3 mr-1" />
                              <span>{list.candidate_count} candidates</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {format(new Date(list.created_at), 'MMM d, yyyy')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 dark:bg-gray-800/50 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={filteredListsNotIn.length > 0 && selectedLists.length === filteredListsNotIn.length}
                onCheckedChange={handleSelectAll}
                className="h-4 w-4"
              />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {selectedLists.length === filteredListsNotIn.length
                  ? t("common.deselectAll", "Deselect all")
                  : t("common.selectAll", "Select all")
                }
              </span>
            </div>
            {selectedLists.length > 0 && (
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {t("candidates.lists.selectedCount", "{{count}} selected", { count: selectedLists.length })}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={handleClose}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button 
              onClick={handleAddToLists}
              disabled={selectedLists.length === 0 || isAdding}
              className="bg-blue-500 hover:bg-blue-500"
            >
              {isAdding ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              {t("candidates.lists.addToLists", "Add to {{count}} lists", { count: selectedLists.length })}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
